import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "design-system/atoms/Accordion"
import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import { Separator } from "design-system/atoms/Separator"
import { Textarea } from "design-system/atoms/Textarea"
import { Card } from "design-system/molecules/Card"
import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import { FormControl, FormField, FormItem, FormLabel } from "design-system/organisms/Form"
import { cn } from "design-system/utils"
import { renderLiquid, tryCatchFallback } from "lopermut"
import { FlaskConical, List, Plus, X } from "lucide-react"
import { useTestInternalEmailMutation } from "modules/campaigns/hooks/useTestInternalEmailMutation"
import { VaultVariableAutocomplete } from "modules/shared/components/VaultVariableAutocomplete"
import { useVaultPreviewContext } from "modules/shared/hooks/useVaultPreviewContext"
import EmployeesFormSelector from "modules/settings/components/Team/Form/EmployeesFormSelector"
import { useEffect, useState } from "react"
import { useFieldArray } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"
import { WorkflowActionInternalEmailConfigSchema, WorkflowNodeSchema } from "schema"
import { toast } from "sonner"
import z from "zod"
import { useWorkflowBuilder } from "../../hooks/useWorkflowBuilder"
import { useEventPayload } from "../../hooks/useEventPayload"

type EmailPreviewProps = {
  formValues: z.infer<typeof WorkflowActionInternalEmailConfigSchema>
  eventPayload: Record<string, unknown>
}

function EmailPreview({ formValues, eventPayload }: EmailPreviewProps) {
  const { t } = useTranslation()
  const previewContext = useVaultPreviewContext(eventPayload)

  const values = {
    recipients: {
      copy: t("campaigns.action.availableActions.SendInternalEmail.config.recipients"),
      value: formValues.email.recipients.map((r) => r.label).join(", "),
      showValue: true,
    },
    subject: {
      copy: t("campaigns.action.availableActions.SendInternalEmail.config.subject"),
      value: formValues.email.subject,
      showValue: true,
    },
    body: {
      copy: t("campaigns.action.availableActions.SendInternalEmail.config.body"),
      value: formValues.email.body,
      showValue: true,
    },
  }

  return (
    <Card className="flex flex-col gap-2">
      <Accordion type="single" collapsible className="w-full text-left">
        <AccordionItem value="preview">
          <AccordionTrigger>
            {t("campaigns.action.availableActions.SendInternalEmail.config.preview")}
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-3">
            {Object.entries(values).map(([key, { copy, value, showValue }]) => {
              const displayedValue = tryCatchFallback(
                () => renderLiquid(value ? String(value) : "", previewContext),
                "",
              )

              return (
                <div key={key} className="flex flex-col gap-0">
                  <span className="text-sm text-muted-foreground">{copy}</span>
                  <span
                    className={cn(
                      "text-sm",
                      showValue && "font-semibold",
                      key === "body" && "bg-muted p-2 rounded-md whitespace-pre-wrap",
                    )}
                  >
                    {displayedValue && displayedValue.toString().length > 0 ? displayedValue : "-"}
                  </span>
                </div>
              )
            })}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  )
}

type SendInternalEmailProps = {
  nodeIndex: number
  node: Extract<z.infer<typeof WorkflowNodeSchema>, { type: "Action" }>
}

export function SendInternalEmail({ nodeIndex, node }: SendInternalEmailProps) {
  const [testDialogOpen, setTestDialogOpen] = useState(false)

  const { t } = useTranslation()
  const { form } = useWorkflowBuilder()
  const { eventPayload, eventName } = useEventPayload({ nodeId: node.id })

  const { mutateAsync: testInternalEmail, isPending: isTestInternalEmailPending } =
    useTestInternalEmailMutation()

  const { append, remove } = useFieldArray({
    control: form.control,
    name: `nodes.${nodeIndex}.config.email.recipients` as const,
  })

  async function handleTestInternalEmail() {
    const formConfig = form.getValues(`nodes.${nodeIndex}.config`)
    const result = WorkflowActionInternalEmailConfigSchema.safeParse(formConfig)

    if (!result.success) {
      toast.error(
        t("campaigns.action.availableActions.SendInternalEmail.config.test.notCompletedFields"),
      )
      return
    }

    await testInternalEmail({
      data: formConfig as z.input<typeof WorkflowActionInternalEmailConfigSchema>,
      eventPayload: eventPayload ?? {},
    })

    toast.success(t("campaigns.action.availableActions.SendInternalEmail.config.test.success"))
    setTestDialogOpen(false)
  }

  useEffect(() => {
    const result = WorkflowActionInternalEmailConfigSchema.safeParse(node.config)

    if (!result.success) {
      form.setValue(`nodes.${nodeIndex}.config`, { email: {} })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const recipients = form.watch(`nodes.${nodeIndex}.config.email.recipients`) || []
  const subject = form.watch(`nodes.${nodeIndex}.config.email.subject`) || ""
  const body = form.watch(`nodes.${nodeIndex}.config.email.body`) || ""

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 justify-start items-start">
        <span className="text-sm text-muted-foreground">
          <Trans
            i18nKey="campaigns.action.availableActions.SendInternalEmail.config.description"
            components={{
              b: <span className="font-medium" />,
              a: (
                <a
                  href="https://shopify.dev/docs/api/liquid"
                  target="_blank"
                  rel="noreferrer"
                  className="underline hover:opacity-80"
                />
              ),
            }}
          />
        </span>
      </div>
      <EmailPreview
        formValues={{
          email: {
            recipients,
            subject,
            body,
          },
        }}
        eventPayload={eventPayload ?? {}}
      />
      <div className="flex flex-row gap-2 justify-between w-full">
        <Button
          variant="testing"
          disabled={!form.formState.isValid}
          onClick={() => setTestDialogOpen(true)}
          className="flex-1"
        >
          <FlaskConical className="size-4" />
          <span>{t("campaigns.action.availableActions.SendInternalEmail.config.test.cta")}</span>
        </Button>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="flex-1">
              <List className="size-4" />
              {t("campaigns.action.availableActions.SendInternalEmail.config.data")}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="flex flex-col gap-2 sm:w-[560px] w-full">
            <span className="text-base">
              <Trans
                i18nKey="campaigns.action.availableActions.SendInternalEmail.config.availableData"
                components={{ b: <span className="font-medium" /> }}
                values={{ eventName }}
              />
            </span>
            <MarkdownRenderer
              className="bg-muted rounded-md overflow-auto max-h-96 border"
              codeClassName="border-none"
            >{`\`\`\`json\n${JSON.stringify(eventPayload, null, 2)}\n\`\`\``}</MarkdownRenderer>
          </PopoverContent>
        </Popover>
      </div>
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent>
          <DialogTitle>
            {t("campaigns.action.availableActions.SendInternalEmail.config.test.title")}
          </DialogTitle>
          <DialogDescription>
            {t("campaigns.action.availableActions.SendInternalEmail.config.test.warning")}
          </DialogDescription>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTestDialogOpen(false)}
              disabled={isTestInternalEmailPending}
            >
              {t("shared.cancel")}
            </Button>
            <Button onClick={handleTestInternalEmail} disabled={isTestInternalEmailPending}>
              {t("shared.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Separator />
      <FormField
        control={form.control}
        name={`nodes.${nodeIndex}.config.email.recipients`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t("campaigns.action.availableActions.SendInternalEmail.config.recipients")}
            </FormLabel>
            <FormControl>
              <div className="flex gap-1 border rounded-md p-1 flex-wrap items-center">
                {field.value?.map((recipient: { value: string; label: string }, index: number) => (
                  <Badge
                    key={recipient.value}
                    onClick={() => remove(index)}
                    variant="secondary"
                    className="h-7"
                  >
                    <span>{recipient.label}</span>
                    <X size={14} />
                  </Badge>
                ))}
                <EmployeesFormSelector
                  emptyPlaceholder={t(
                    "campaigns.action.availableActions.SendInternalEmail.employeesSelector.emptyPlaceholder",
                  )}
                  placeholder={t(
                    "campaigns.action.availableActions.SendInternalEmail.employeesSelector.placeholder",
                  )}
                  searchPlaceholder={t(
                    "campaigns.action.availableActions.SendInternalEmail.employeesSelector.searchPlaceholder",
                  )}
                  icon={<Plus />}
                  className="w-fit"
                  buttonClassName="p-1.5"
                  value={null}
                  onChange={(option) => {
                    if (option?.value) {
                      const findIndex = field.value?.findIndex(
                        (e: { value: string }) => e.value === option.value,
                      )

                      if (findIndex >= 0) {
                        remove(findIndex)
                      } else {
                        append(option)
                      }
                      form.trigger()
                    }
                  }}
                />
              </div>
            </FormControl>
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={`nodes.${nodeIndex}.config.email.subject`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t("campaigns.action.availableActions.SendInternalEmail.config.subject")}
            </FormLabel>
            <FormControl>
              <VaultVariableAutocomplete variables={[]}>
                <Input
                  {...field}
                  onChange={(e) => {
                    form.setValue(field.name, e.target.value, { shouldDirty: true })
                    form.trigger()
                  }}
                />
              </VaultVariableAutocomplete>
            </FormControl>
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={`nodes.${nodeIndex}.config.email.body`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t("campaigns.action.availableActions.SendInternalEmail.config.body")}
            </FormLabel>
            <FormControl>
              <VaultVariableAutocomplete variables={[]}>
                <Textarea
                  {...field}
                  onKeyDown={(ev) => ev.stopPropagation()}
                  onChange={(e) => {
                    form.setValue(field.name, e.target.value, { shouldDirty: true })
                    form.trigger()
                  }}
                />
              </VaultVariableAutocomplete>
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  )
}
